/**
 * =============================================================================
 * WlRevenueByAddonPieChart
 *
 * Grafico de pizza (donut) com a distribuicao do MRR por add-on.
 * Legenda customizada com valor em R$ e percentual. Add-ons de menor
 * receita são agrupados em "Outros" para manter a legenda legível.
 *
 * @version 1.1.0
 * RELEASE:
 *   2026-07-21 v1.0.0 - Versao inicial.
 *   2026-07-21 v1.1.0 - Legenda customizada com valor em R$ e percentual.
 * =============================================================================
 */
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import type { WlFinancialAddonRow } from '@/hooks/wl/useWlFinancialKpis';

const COLORS = [
  'hsl(217 91% 60%)',
  'hsl(142 71% 45%)',
  'hsl(38 92% 50%)',
  'hsl(280 65% 60%)',
  'hsl(340 82% 52%)',
  'hsl(190 90% 45%)',
  'hsl(0 84% 60%)',
  'hsl(158 64% 40%)',
  'hsl(220 9% 46%)', // slate-500 para "Outros"
];

const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v || 0));

interface Props {
  data: WlFinancialAddonRow[];
  /** Quantidade maxima de fatias individuais (o restante vira "Outros"). */
  topN?: number;
}

/** Grafico de pizza — MRR por add-on (top N + "Outros"). */
export function WlRevenueByAddonPieChart({ data, topN = 8 }: Props) {
  const filtered = (data ?? []).filter((r) => Number(r.mrr) > 0);

  if (filtered.length === 0) {
    return <p className="text-sm text-muted-foreground">Sem dados</p>;
  }

  // Ordena por MRR desc para escolher os top N.
  const sorted = [...filtered].sort((a, b) => Number(b.mrr) - Number(a.mrr));
  const top = sorted.slice(0, topN);
  const rest = sorted.slice(topN);
  const restTotal = rest.reduce((acc, r) => acc + Number(r.mrr || 0), 0);

  const rows: Array<{ name: string; value: number }> = top.map((r) => ({
    name: r.addon_name,
    value: Number(r.mrr || 0),
  }));
  if (restTotal > 0) rows.push({ name: `Outros (${rest.length})`, value: restTotal });

  // Total para cálculo do percentual (v1.1.0).
  const total = rows.reduce((acc, r) => acc + r.value, 0);

  return (
    <div className="flex items-center gap-4 h-[260px] w-full">
      {/* Donut à esquerda */}
      <div className="flex-1 h-full min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={90}
              paddingAngle={2}
            >
              {rows.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => fmtBRL(value)}
              contentStyle={{ fontSize: 12 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legenda customizada — v1.1.0: mostra R$ e % ao lado do nome. */}
      <ul className="flex flex-col gap-1.5 min-w-[180px] max-w-[240px] text-xs">
        {rows.map((r, i) => {
          const pct = total > 0 ? (r.value / total) * 100 : 0;
          return (
            <li key={r.name} className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm shrink-0"
                style={{ backgroundColor: COLORS[i % COLORS.length] }}
              />
              <span className="truncate flex-1">{r.name}</span>
              <span className="font-medium tabular-nums">{fmtBRL(r.value)}</span>
              <span className="text-muted-foreground tabular-nums w-12 text-right">
                ({pct.toFixed(1)}%)
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
